// https://developers.google.com/youtube/iframe_api_reference#Example_Video_Player_Constructors
import React, { useEffect, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import VideoWrapper from './Video.styles';
import { VideoWrapperInner, Notification } from './Video.styles';
import PlayBtnSvg from "./InlineSvg/VideoPlayBtn.jsx";
import SVGspiralImg from "./InlineSvg/SVGspiralImg.jsx";
import { useAsyncScript, useObserver } from '../../../hooks';
import BigBeanVector from '../../../assets/images/BigBeanVector.svg';


const Video = ({ youtubeId, videoPoster, width = '960', height = '540',poster, ratio = [16, 9] }) => {

  const videoRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [youtubeReady, setYoutubeReady] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [vidPoster, setVidPoster] = useState(poster || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : ''));

  const handlePosterError = () => {
    setVidPoster(`https://img.youtube.com/vi/${youtubeId}/default.jpg`);
  };


  const loadVideo = useCallback(() => {
    if (!youtubeId || typeof window === 'undefined' || !youtubeReady) return;
    window.YT.ready(() => {
      const player = new window.YT.Player(`youtube-${youtubeId}`, {
        videoId: youtubeId,
        height,
        width,
        events: {
          onReady: (event) => {
            event.target.playVideo();
          }
        },
        playerVars: {
          autoplay: 1,
          modestbranding: 1,
          rel: 0
        },
      });
      setPlayer(player);
      setVideoLoaded(true);
    });
  }, [youtubeId, height, width, youtubeReady]);

  const handlePlayClick = () => {
    if (!youtubeReady) {
      setShowNotification(true);
    } else if (!videoLoaded) {
      loadVideo();
    }
  };


useEffect(() => {
  if (typeof window === 'undefined') return;

  const handleYouTubeIframeAPIReady = () => {
    setYoutubeReady(true);
  };

  document.addEventListener('youtubeIframeAPIReady', handleYouTubeIframeAPIReady);

  return () => {
    document.removeEventListener('youtubeIframeAPIReady', handleYouTubeIframeAPIReady);
    if (player) {
      player.destroy();
    }
  };
}, [player]);


  return (
      <VideoWrapper ref={videoRef} className="video-wrapper">
        <VideoWrapperInner ratio={ratio}>
          {!videoLoaded && (
              <div
                  className="video-placeholder"
                  style={{ backgroundImage: `url(${vidPoster})` }}
                  onClick={handlePlayClick}
              >
                {vidPoster && (
                    <img src={vidPoster} className="video-poster-fallback" onError={handlePosterError} alt="Video poster" />
                )}
                <button className="play-video-button" aria-label="Play video">
                  <span className="self"><PlayBtnSvg/></span>
                </button>
              </div>
          )}
          <div id={`youtube-${youtubeId}`} />
        </VideoWrapperInner>
        {showNotification && (
          <Notification>
            <div className="bean-graphic">
              <SVGspiralImg />
              <img className="inner-bean" src={BigBeanVector} width="1728" height="913" alt="big bean background decoration graphic" />
            </div>
            <p>To view this video, please accept the cookie policy to enable YouTube content.</p>
            <span className="small-text">Look for this icon in the bottom left</span>
            <button onClick={() => setShowNotification(false)}>Close &#x2715;</button>
          </Notification>
        )}
      </VideoWrapper>
  );
};

export default Video;

Video.propTypes = {
  youtubeId: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  ratio: PropTypes.array,
  poster: PropTypes.string,
};